import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import SERVER from '../hooks/useServer';
import sad from '../assets/icons/sad.png';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCart } from '../hooks/cart';
import ProductCard from '../components/ProductCard';
import { FaCaretUp } from "react-icons/fa6";
import { FaCaretDown } from "react-icons/fa6";
import { MdOutlineFiberNew } from "react-icons/md";
import { PiTimerBold } from "react-icons/pi";
import { IoIosArrowRoundForward, IoIosArrowRoundBack  } from "react-icons/io";
import { IoChevronForward } from "react-icons/io5";
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import filtericon from '../assets/icons/filter.svg';

const Products = () => {
    const { t, i18n } = useTranslation();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const { state } = useLocation();
    const {categoryTitle, subCategoryTitle} = useParams()
    const { fetchProducts, fetchCategories, fetchSubCategories } = useApi();
    const categoryId = state?.categoryId;
    const subCategoryId = state?.subCategoryId;
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState(null);
    const [orderBy, setOrderBy] = useState(null);
    const [filters, setFilters] = useState({});
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [characteristics, setCharacteristics] = useState([]);
    const [expandedSections, setExpandedSections] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const [condition, setCondition] = useState(true)
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(categoryId || null);
    const [selectedSubCategoryFilter, setSelectedSubCategoryFilter] = useState(subCategoryId || null);
    const [showFilters, setShowFilters] = useState(true); // Mobile filter toggle
    
    const toggleSection = (title) => {
        setExpandedSections(prev => ({
            ...prev,
            [title]: !prev[title]
        }));
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                
                // Fetch categories and subcategories
                const [categoriesData, subCategoriesData] = await Promise.all([
                    fetchCategories(),
                    fetchSubCategories()
                ]);
                
                setCategories(categoriesData);
                setSubCategories(subCategoriesData);
                
                // Fetch products with filters
                const params = {};
                
                // Priority: subcategory > category
                if (subCategoryId) {
                    params.subcategory = typeof subCategoryId === 'object' ? subCategoryId.id : subCategoryId;
                } else if (categoryId) {
                    params.category = categoryId;
                }
                // If neither exists, fetch all products (no params)
                
                const productsData = await fetchProducts(params);
                setProducts(productsData);
                const processedChars = processCharacteristics(productsData);
                setCharacteristics(processedChars);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [categoryId, subCategoryId, i18n.language, fetchProducts, fetchCategories, fetchSubCategories]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy, orderBy, filters]);

    const processCharacteristics = (products) => {
        const allChars = {};

        products.forEach(product => {
            const merged = { ...product.main_caracteristiques, ...product.secondary_caracteristiques };
            Object.values(merged).forEach(char => {
                // Use translated title if available
                const title = (i18n.language === 'en' && char.title_en) ? char.title_en : char.title;
                const originalTitle = char.title; // Keep original for matching
                
                if (!allChars[originalTitle]) {
                    allChars[originalTitle] = {
                        title: title,
                        values: new Set(),
                        numericValues: [],
                        isNumeric: true
                    };
                }

                const value = char.value;
                if (Array.isArray(value)) {
                    value.forEach(v => processValue(v, allChars[originalTitle]));
                } else {
                    processValue(value, allChars[originalTitle]);
                }
            });
        });

        return Object.entries(allChars).map(([originalTitle, data]) => {
            if (data.isNumeric && data.numericValues.length > 0) {
                const min = Math.min(...data.numericValues);
                const max = Math.max(...data.numericValues);
                setCondition(min !== max || data.values.size > 1)
                return {
                    title: data.title,
                    originalTitle,
                    type: 'numeric',
                    min,
                    max,
                    values: data.numericValues,
                    hasMultipleValues: min !== max
                };
            } else {
                return {
                    title: data.title,
                    originalTitle,
                    type: 'categorical',
                    values: Array.from(data.values),
                    hasMultipleValues: data.values.size > 1
                };
            }
        });
    };

    const processValue = (value, data) => {
        const strVal = value.toString();
        data.values.add(strVal);
        const numVal = parseFloat(strVal.replace(',', '.'));
        if (isNaN(numVal)) {
            data.isNumeric = false;
        } else {
            data.numericValues.push(numVal);
        }
    };

    const filteredAndSortedProducts = useMemo(() => {
        let filtered = products.filter(product => {
            const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                product.price.toString().includes(searchTerm);
            if (!matchesSearch) return false;

            // Characteristic filters
            const productChars = { ...product.main_caracteristiques, ...product.secondary_caracteristiques };
            return Object.entries(filters).every(([originalTitle, filter]) => {
                const char = Object.values(productChars).find(c => c.title === originalTitle);
                if (!char) return false;

                const value = char.value.toString();
                if (filter.type === 'numeric') {
                    const numVal = parseFloat(value.replace(',', '.'));
                    return (filter.filterMin === undefined || numVal >= filter.filterMin) && 
                        (filter.filterMax === undefined || numVal <= filter.filterMax);
                } else {
                    const productValues = value.split(',').map(v => v.trim());
                    return productValues.some(v => filter.selected.includes(v));
                }
            });
        });

        if (sortBy) {
            filtered = [...filtered].sort((a, b) => {
                const priceA = parseFloat(a.price);
                const priceB = parseFloat(b.price);
                
                switch (sortBy) {
                    case 'price_asc': return priceA - priceB;
                    case 'price_desc': return priceB - priceA;
                    default: return 0;
                }
            });
        }

        if (orderBy) {
            filtered = [...filtered].sort((a, b) => {
                switch (orderBy) {
                    case 'date_new': return new Date(b.created_at) - new Date(a.created_at);
                    case 'date_old': return new Date(a.created_at) - new Date(b.created_at);
                    default: return 0;
                }
            });
        }

        return filtered;
    }, [products, searchTerm, filters, sortBy, orderBy]);

    // Pagination logic
    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedProducts.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedProducts, currentPage]);

    const totalPages = useMemo(() => {
        return Math.ceil(filteredAndSortedProducts.length / itemsPerPage);
    }, [filteredAndSortedProducts]);

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy(null);
        setOrderBy(null);
        setFilters(prev => {
            const newFilters = {};
            for (const [originalTitle, filter] of Object.entries(prev)) {
                if (filter.type === 'numeric') {
                    const char = characteristics.find(c => c.originalTitle === originalTitle);
                    if (char) {
                        newFilters[originalTitle] = {
                            ...filter,
                            filterMin: char.min,
                            filterMax: char.max
                        };
                    }
                }
            }
            return newFilters;
        });
        setExpandedSections({});
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const getPageNumbers = () => {
            const pages = [1];
            if (totalPages >= 2) {
                pages.push(2);
            }

            if (currentPage > 2 && currentPage < totalPages) {
                if (currentPage > 3) {
                    pages.push('...');
                }
                pages.push(currentPage);
            }

            if (currentPage < totalPages - 1) {
                pages.push('...');
            }

            if (totalPages > 2 && !pages.includes(totalPages)) {
                pages.push(totalPages);
            }

            return pages;
        };

        return (
            <div className="flex items-center justify-center gap-2 mt-8">
                <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                    <IoIosArrowRoundBack className="text-2xl" />
                </button>
                
                {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">...</span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
                                currentPage === page 
                                    ? 'bg-primary text-white border-primary font-semibold' 
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    )
                ))}
                
                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                    className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                    <IoIosArrowRoundForward className="text-2xl" />
                </button>
            </div>
        );
    };

    const CustomSlider = styled(Slider)({
        '.MuiSlider-track': {
            color: '#1920ac'
        },
        '& .MuiSlider-thumb': {
            height: 18,
            width: 18,
            backgroundColor: '#1920ac',
            border: 'none',
            '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
            },
            '&.Mui-active': {
                height: 24,
                width: 24,
            },
        },
        '& .MuiSlider-valueLabel': {
            lineHeight: 1.2,
            fontSize: 12,
            background: 'unset',
            padding: 0,
            width: 32,
            height: 32,
            borderRadius: '50% 50% 50% 0',
            backgroundColor: '#1920ac',
            transformOrigin: 'bottom left',
            transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
            '&:before': { display: 'none' },
            '&.MuiSlider-valueLabelOpen': {
                transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
            },
            '& > *': {
                transform: 'rotate(45deg)',
            },
        },
        '& .MuiSlider-track': {
            height: 8,
        },
        '& .MuiSlider-rail': {
            height: 8,
            opacity: 0.3,
        },
    });

    function NumericRangeSlider({ 
        title, 
        min: charMin, 
        max: charMax, 
        values, 
        filter, 
        onFilterChange 
    }) {
        // Initialize with either existing filter values or full range
        const [sliderValue, setSliderValue] = useState([
            filter?.sliderMin ?? charMin,
            filter?.sliderMax ?? charMax
        ]);

        // Format numbers appropriately
        const formatValue = (val) => {
            const absVal = Math.abs(val);
            if (absVal === 0) return '0';
            
            // Find the first non-zero digit after decimal
            if (absVal < 1) {
                const decimals = -Math.floor(Math.log10(absVal));
                return val.toFixed(Math.min(decimals, 5));
            }
            
            if (absVal >= 100) return val.toFixed(0);
            if (absVal >= 10) return val.toFixed(1);
            return val.toFixed(2);
        };

        const handleChange = (event, newValue) => {
            setSliderValue(newValue);
        };

        const handleChangeCommitted = (event, newValue) => {
            // Update both the slider position AND the filter range
            onFilterChange({
                type: 'numeric',
                filterMin: newValue[0],
                filterMax: newValue[1],
                sliderMin: newValue[0],
                sliderMax: newValue[1]
            });
        };

        return (
            <Box sx={{ width: '90%', px: 2, py: 1 }}>
                <CustomSlider
                    value={sliderValue}
                    onChange={handleChange}
                    onChangeCommitted={handleChangeCommitted}
                    valueLabelDisplay="auto"
                    valueLabelFormat={formatValue}
                    min={charMin}
                    max={charMax}
                    step={(charMax - charMin) / 100}
                    disableSwap
                />
                <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                    fontSize: '0.875rem',
                }}>
                    <span>{formatValue(sliderValue[0])}</span>
                    <span>{formatValue(sliderValue[1])}</span>
                </Box>
            </Box>
        );
    }    
    const renderFilterSection = (char) => {
        const isExpanded = expandedSections[char.title];
        const currentFilter = filters[char.originalTitle] || {};
        return (
            <div key={char.originalTitle} className={`filter-section ${isExpanded ? 'expanded' : ''}`}>
                <h3 onClick={() => toggleSection(char.title)} style={{ cursor: 'pointer' }}>
                    {char.title}
                </h3>

                {isExpanded && char.type === 'numeric' ? (
                    <NumericRangeSlider
                    title={char.title}
                    min={char.min}
                    max={char.max}
                    values={char.values}
                    filter={currentFilter}
                    onFilterChange={(newFilter) => {
                        setFilters(prev => ({
                            ...prev,
                            [char.originalTitle]: newFilter
                        }));
                    }}
                />
                ) : isExpanded && char.type === 'categorical' && Array.isArray(char.values) ? (
                        <div className="categorical-filter">
                            {char.values.map(value => (
                                <label key={value}>
                                    <input
                                        type="checkbox"
                                        checked={filters[char.originalTitle]?.selected?.includes(value) || false}
                                        onChange={(e) => {
                                            const selected = e.target.checked
                                                ? [...(filters[char.originalTitle]?.selected || []), value]
                                                : (filters[char.originalTitle]?.selected || []).filter(v => v !== value);
                                            setFilters(prev => ({
                                                ...prev,
                                                [char.originalTitle]: { type: 'categorical', selected }
                                            }));
                                        }}
                                    />
                                    {value}
                                </label>
                            ))}
                        </div>
                    ) : null}
                </div>
            );
    };

    if (loading) return <div className="loading w-screen h-screen items-center justify-center flex">Loading...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="w-full max-w-7xl mx-auto px-4 pb-10 pt-24">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm mb-8 flex-wrap">
                <span className='cursor-pointer text-black hover:underline' onClick={() => navigate('/')}>{t('breadcrumbs.home')}</span>
                <IoChevronForward className="text-gray-400" />
                <span className={`${!categoryTitle ? 'font-normal' : 'cursor-pointer hover:underline'}`} onClick={() => navigate('/products')}>{t('breadcrumbs.products')}</span>
                {categoryTitle && (
                    <>
                        <IoChevronForward className="text-gray-400" />
                        <span className={`${!subCategoryTitle ? 'font-normal' : 'cursor-pointer hover:underline'}`} 
                              onClick={() => navigate(`/products/${categoryTitle}`, { state: { categoryId } })}>
                            {categoryTitle}
                        </span>
                    </>
                )}
                {subCategoryTitle && (
                    <>
                        <IoChevronForward className="text-gray-400" />
                        <span className='font-normal'>{subCategoryTitle}</span>
                    </>
                )}
            </div>

            {products.length > 0 ? (
                <div className="w-full">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-primary text-2xl md:text-4xl font-normal mb-2">
                            {t('products.catalogTitle')}
                        </h1>
                        <h2 className="text-gray-800 text-xl md:text-2xl font-normal">
                            {subCategoryTitle || categoryTitle || t('products.allProducts')}
                        </h2>
                    </div>

                    {/* Main Container */}
                    <div className="flex gap-2 lg:gap-8">
                        {/* Filters Sidebar */}
                        <div className={`${showFilters ? 'w-1/2 lg:w-80' : 'hidden lg:block lg:w-80'} flex-shrink-0`}>
                            <div className="p-3 lg:p-6 sticky top-20">
                                {/* Filters Header - Clickable toggle on mobile */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="w-full flex items-center gap-2 lg:gap-4 mb-4 lg:mb-6 lg:cursor-default"
                                >
                                    <img src={filtericon} className='w-5 h-5 lg:w-6 lg:h-6'/>
                                    <h2 className="text-black font-normal text-base lg:text-lg">{t('products.filters')}</h2>
                                </button>

                                {/* Filter Sections */}
                                <div className="space-y-3 lg:space-y-4">
                                    {characteristics
                                        .filter(char => {
                                            return char.hasMultipleValues && products.some(product => 
                                                product.main_caracteristiques && 
                                                Object.values(product.main_caracteristiques).some(c => c.title === char.originalTitle)
                                            );
                                        })
                                        .slice(0, 5) // Only show first 5 filters
                                        .map((char) => {
                                            const isExpanded = expandedSections[char.title];
                                            const currentFilter = filters[char.originalTitle] || {};
                                            
                                            return (
                                                <div key={char.originalTitle} className="pb-3 lg:pb-4">
                                                    <label className="flex items-center gap-2 lg:gap-3 cursor-pointer group">
                                                        <input
                                                            type="checkbox"
                                                            checked={isExpanded}
                                                            onChange={() => toggleSection(char.title)}
                                                            className="w-4 h-4 lg:w-5 lg:h-5 rounded-md border-gray-300 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                                                            style={{ accentColor: '#1920ac' }}
                                                        />
                                                        <span className="text-black text-sm lg:text-base font-normal group-hover:text-primary transition-colors">
                                                            {char.title}
                                                        </span>
                                                    </label>

                                                    {isExpanded && char.type === 'numeric' && (
                                                        <div className="mt-3 lg:mt-4">
                                                            <NumericRangeSlider
                                                                title={char.title}
                                                                min={char.min}
                                                                max={char.max}
                                                                values={char.values}
                                                                filter={currentFilter}
                                                                onFilterChange={(newFilter) => {
                                                                    setFilters(prev => ({
                                                                        ...prev,
                                                                        [char.originalTitle]: newFilter
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                    {isExpanded && char.type === 'categorical' && Array.isArray(char.values) && (
                                                        <div className="mt-3 lg:mt-4 space-y-2 pl-6 lg:pl-8">
                                                            {char.values.map(value => (
                                                                <label key={value} className="flex items-center gap-2 cursor-pointer group">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={filters[char.originalTitle]?.selected?.includes(value) || false}
                                                                        onChange={(e) => {
                                                                            const selected = e.target.checked
                                                                                ? [...(filters[char.originalTitle]?.selected || []), value]
                                                                                : (filters[char.originalTitle]?.selected || []).filter(v => v !== value);
                                                                            setFilters(prev => ({
                                                                                ...prev,
                                                                                [char.originalTitle]: { type: 'categorical', selected }
                                                                            }));
                                                                        }}
                                                                        className="w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-md border-gray-300 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                                                                        style={{ accentColor: '#1920ac' }}
                                                                    />
                                                                    <span className="text-gray-700 text-xs lg:text-sm group-hover:text-primary transition-colors">
                                                                        {value}
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    }

                                    {/* Advanced Filters - show remaining main characteristics */}
                                    {characteristics.filter(char => {
                                        return char.hasMultipleValues && products.some(product => 
                                            product.main_caracteristiques && 
                                            Object.values(product.main_caracteristiques).some(c => c.title === char.originalTitle)
                                        );
                                    }).length > 5 && (
                                        <div className="pt-3 lg:pt-4">
                                            <button 
                                                className='text-black text-sm lg:text-base font-normal hover:text-primary transition-colors flex items-center gap-2'
                                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                            >
                                                {t('products.advancedFilters')}
                                                <svg className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>

                                            {showAdvancedFilters && (
                                                <div className="mt-3 lg:mt-4 space-y-3 lg:space-y-4">
                                                    {characteristics
                                                        .filter(char => {
                                                            return char.hasMultipleValues && products.some(product => 
                                                                product.main_caracteristiques && 
                                                                Object.values(product.main_caracteristiques).some(c => c.title === char.originalTitle)
                                                            );
                                                        })
                                                        .slice(5) // Show filters after the first 5
                                                        .map((char) => {
                                                            const isExpanded = expandedSections[char.title];
                                                            const currentFilter = filters[char.originalTitle] || {};
                                                            
                                                            return (
                                                                <div key={char.originalTitle} className="pb-3 lg:pb-4">
                                                                    <label className="flex items-center gap-2 lg:gap-3 cursor-pointer group">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isExpanded}
                                                                            onChange={() => toggleSection(char.title)}
                                                                            className="w-4 h-4 lg:w-5 lg:h-5 rounded-md border-gray-300 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                                                                            style={{ accentColor: '#1920ac' }}
                                                                        />
                                                                        <span className="text-black text-sm lg:text-base group-hover:text-primary transition-colors">
                                                                            {char.title}
                                                                        </span>
                                                                    </label>

                                                                    {isExpanded && char.type === 'numeric' && (
                                                                        <div className="mt-3 lg:mt-4">
                                                                            <NumericRangeSlider
                                                                                title={char.title}
                                                                                min={char.min}
                                                                                max={char.max}
                                                                                values={char.values}
                                                                                filter={currentFilter}
                                                                                onFilterChange={(newFilter) => {
                                                                                    setFilters(prev => ({
                                                                                        ...prev,
                                                                                        [char.originalTitle]: newFilter
                                                                                    }));
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {isExpanded && char.type === 'categorical' && Array.isArray(char.values) && (
                                                                        <div className="mt-3 lg:mt-4 space-y-2 pl-6 lg:pl-8">
                                                                            {char.values.map(value => (
                                                                                <label key={value} className="flex items-center gap-2 cursor-pointer group">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={filters[char.originalTitle]?.selected?.includes(value) || false}
                                                                                        onChange={(e) => {
                                                                                            const selected = e.target.checked
                                                                                                ? [...(filters[char.originalTitle]?.selected || []), value]
                                                                                                : (filters[char.originalTitle]?.selected || []).filter(v => v !== value);
                                                                                            setFilters(prev => ({
                                                                                                ...prev,
                                                                                                [char.originalTitle]: { type: 'categorical', selected }
                                                                                            }));
                                                                                        }}
                                                                                        className="w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-md border-gray-300 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                                                                                        style={{ accentColor: '#1920ac' }}
                                                                                    />
                                                                                    <span className="text-gray-700 text-xs lg:text-sm group-hover:text-primary transition-colors">
                                                                                        {value}
                                                                                    </span>
                                                                                </label>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Products Grid */}
                        <div className={`${showFilters ? 'w-1/2 lg:flex-1' : 'flex-1'}`}>
                            {/* Product Count Header */}
                            <div className="flex justify-end items-center mb-4">
                                <span className="text-gray-500 text-xs lg:text-sm">{filteredAndSortedProducts.length} {t('products.items')}</span>
                            </div>
                            
                            <div className={`grid gap-2 md:gap-6 ${showFilters ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-3'}`}>
                                {paginatedProducts.map((product) => (
                                    <ProductCard 
                                        key={product.id} 
                                        product={product} 
                                        onAddToCart={() => addToCart(product)}
                                        onClick={() => {
                                            const prodCategoryTitle = categoryTitle || product.category_title;
                                            const prodSubCategoryTitle = subCategoryTitle || product.sub_category_title;
                                            const prodCategoryId = categoryId || product.category_id;
                                            const prodSubCategoryId = subCategoryId || product.sub_category_id;
                                            
                                            navigate(`/product/${prodCategoryTitle}/${prodSubCategoryTitle}/${product.title}`, { 
                                                state: { 
                                                    categoryId: prodCategoryId, 
                                                    subCategoryId: prodSubCategoryId, 
                                                    productId: product.id 
                                                } 
                                            });
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {renderPagination()}
                        </div>
                    </div>
                </div>
            ) : (
                <div className='flex flex-col items-center justify-center gap-4 bg-white text-center rounded-xl w-full p-10'>
                    <span className="text-xl font-medium text-gray-700">{t('products.noProducts')}</span>
                    <img src={sad} alt='desole' className="w-1/4" />
                </div>
            )}
        </div>
    );
};

export default Products;